import { css } from '@emotion/react';
import { NO_BREAK_SPACE } from '~/_shared/constants/text.constants';
import type { Theme } from '~/_shared/themes/theme.model';
import type { ExportReportTableValue } from '~/exportReport/table/ReportTable.model';
import type { ReportTemplateDataRow } from '../template/reportTemplate';

const rowStyle = ({ style, isInteractive }: { style?: ReportTemplateDataRow['style']; isInteractive?: boolean }) => (theme: Theme) => css({
  borderBottom: `1px solid ${theme.reportColors.borderPrimary}`,
  breakInside: 'avoid',
  color: theme.reportColors.textPrimary,
  cursor: isInteractive ? 'pointer' : undefined,
  fontWeight: style === 'bold' || style === 'highlight' ? 700 : undefined,
  textDecoration: style === 'highlight' ? 'underline' : undefined,
  fontStyle: style === 'accent' ? 'italic' : undefined,

  'tbody &:nth-of-type(2n)': {
    background: theme.reportColors.tableRowBackgroundPrimary,
  },
  'tbody &:nth-of-type(2n + 1)': {
    background: theme.reportColors.tableRowBackgroundSecondary,
  },
});

const tableDataCellStyle = ({ isError }: { isError?: boolean } = {}) => (theme: Theme) => css({
  textAlign: 'right',
  border: `0 solid ${theme.reportColors.borderPrimary}`,
  padding: 5,
  borderTopWidth: 0,
  borderBottomWidth: 0,
  color: isError ? 'red' : undefined,
  fontWeight: isError ? 700 : undefined,
  '&:first-of-type': {
    borderLeftWidth: 1,
    textAlign: 'left',
  },
  '&:last-of-type': {
    borderRightWidth: 1,
  },
  'tr:first-of-type &': {
    borderTopWidth: 1,
  },
  'tr:last-of-type &': {
    borderBottomWidth: 1,
  },
  'tr:first-of-type &:first-of-type': {
    borderRadius: '8px 0 0 0',
  },
  'tr:last-of-type &:first-of-type': {
    borderRadius: '0 0 0 8px',
  },
  'tr:first-of-type &:last-of-type': {
    borderRadius: '0 8px 0 0',
  },
  'tr:last-of-type &:last-of-type': {
    borderRadius: '0 0 8px 0',
  },
});

type ExportReportTableDataRowProps = Readonly<{
  style?: ReportTemplateDataRow['style'];
  isInteractive?: boolean;
  isLabelError?: boolean;
  label: string;
  onClick?: () => void;
  values: ReadonlyArray<ExportReportTableValue>;
}>;

export const ExportReportTableDataRowComponent = (props: ExportReportTableDataRowProps) => {
  return (
    <tr css={rowStyle({ style: props.style, isInteractive: props.isInteractive })} onClick={props.onClick}>
      <td css={tableDataCellStyle({ isError: props.isLabelError })}>
        {props.label}
        {!props.label && NO_BREAK_SPACE}
      </td>
      {props.values.map((item, index) => (
        <td key={index} css={tableDataCellStyle({ isError: item.isError })}>
          <span>
            {item.value}
          </span>
        </td>
      ))}
    </tr>
  );
};
